import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DOCUMENT,
  HostBinding,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  RESPONSE_INIT,
  Renderer2,
  afterNextRender,
  inject,
} from '@angular/core';
import { MatSidenavModule } from '@angular/material/sidenav';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Observable, Subscription, fromEvent, lastValueFrom } from 'rxjs';
import { debounceTime, filter, take } from 'rxjs/operators';
import { LayoutService } from 'src/app/services/device-detector/layout.service';
import { environment } from 'src/environments/environment';
import { INDEX_RESET_KEY } from './models/error';
import { IconModule } from './modules/icon.module';
import { AnalyticService } from './services/analytics/analytics.service';
import { SalesIQService } from './services/analytics/salesiq.service';
import { BasketQuery } from './services/basket/basket.query';
import { BasketService } from './services/basket/basket.service';
import { ContentService, sections } from './services/content/content.service';
import { CookiePolicyService } from './services/cookie/cookie.service';
import { DialogService } from './services/dialog/dialog.service';
import { KioskService } from './services/kiosk/kiosk.service';
import { MobileService } from './services/mobile.service';
import { ProductQuery } from './services/product';
import { ResetService } from './services/reset/reset.service';
import { ShippingService } from './services/shipping';
import { ThemeService } from './services/theme/theme.service';
import { AddressBarComponent } from './shared/components';
import { StreamerBannerComponent } from './shared/components/streamer-banner/streamer-banner.component';
import { FooterComponent } from './shared/layout/footer';
import { BasketComponent, HeaderComponent, MobileMenuComponent } from './shared/layout/header';
import { MenyHeaderComponent } from './shared/layout/meny-header';
import { MenyNavigationComponent } from './shared/layout/meny-navigation/meny-navigation.component';
import { NavigationHeaderComponent } from './shared/layout/navigation-header/navigation-header.component';
import { NavigationComponent } from './shared/layout/navigation/navigation.component';
import { OnlineHeaderComponent } from './shared/layout/online-header/online-header.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    RouterOutlet,
    MatSidenavModule,
    AsyncPipe,
    BasketComponent,
    MobileMenuComponent,
    AddressBarComponent,
    MenyHeaderComponent,
    MenyNavigationComponent,
    OnlineHeaderComponent,
    HeaderComponent,
    NavigationComponent,
    NavigationHeaderComponent,
    FooterComponent,
    StreamerBannerComponent,

    // Install app icons
    IconModule,
  ],
  host: {
    '[class.blackout]': 'kioskService.blackout',
  },
})
export class AppComponent implements OnInit, OnDestroy {
  private document = inject<Document>(DOCUMENT);
  private contentService = inject(ContentService);
  private renderer = inject(Renderer2);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private productQuery = inject(ProductQuery);
  private basketQuery = inject(BasketQuery);
  private dialogService = inject(DialogService);
  private cookiePolicyService = inject(CookiePolicyService);
  private shippingService = inject(ShippingService);
  private analyticService = inject(AnalyticService);
  private deviceService = inject(DeviceDetectorService);
  private layoutService = inject(LayoutService);
  private themeService = inject(ThemeService);
  private ngZone = inject(NgZone);
  private resetService = inject(ResetService);
  private salesIQService = inject(SalesIQService, { optional: true });
  kioskService = inject(KioskService);
  mobileService = inject(MobileService);
  basketService = inject(BasketService);
  private response = inject(RESPONSE_INIT, { optional: true });

  contentUrl = `${environment.cmsOrigin}`;
  apiUrl = `${environment.apiOrigin}`;
  disclaimerShown = 'Disclaimer';

  subscription: Subscription;

  showShipping = false;
  showBasket = false;
  showNavigation = false;
  showFooter = false;
  showFilter = false;
  noContent = false;

  navigationHeader = false;
  showNavigationHeader = false;
  isDagrofa: boolean;
  isOnline: boolean;

  address: string;

  searchOpen$: Observable<boolean>;
  basketOpen$: Observable<boolean>;
  mobileMenuOpen$: Observable<boolean>;

  cookieDialogOpen: boolean;

  stickyHeader: boolean;
  @HostBinding('style.--sticky-header-height') stickyHeaderHeight: string;

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    if (!this.navigationHeader || this.isDagrofa) {
      return;
    }

    this.setHeader();
  }

  constructor() {
    if (this.contentService.noContent) {
      this.noContent = true;
      return;
    }

    const favicon = this.contentService.getFavicon();
    if (favicon) this.document.getElementById('app-fav-icon').setAttribute('href', `${this.contentUrl}${favicon}`);
    this.address = this.contentService.getStoreAddress();

    afterNextRender({
      write: () => {
        if (!this.kioskService.isKiosk) {
          this.initDialogs();
        }
      },
    });

    this.isDagrofa = this.contentService.isDagrofa;
    this.navigationHeader = this.contentService.isNavigationHeader();
    if (!this.navigationHeader || this.isDagrofa) {
      return;
    }

    const headerContent = this.contentService.get(sections.header);
    this.stickyHeader = headerContent.get('sticky');

    this.setHeader();
  }

  ngOnInit() {
    this.subscription = new Subscription();
    if (this.noContent && this.response) {
      this.response.status = 404;
      return;
    }

    this.isOnline = this.themeService.isOnline;

    this.searchOpen$ = this.productQuery.selectSearchOpen$;
    this.basketOpen$ = this.basketQuery.selectOpen$;
    this.mobileMenuOpen$ = this.mobileService.mobileMenuOpen;

    this.setAnalytics();
    this.clearReset();

    this.subscription.add(
      this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe((event) => {
        this.showShipping = this.activatedRoute.firstChild.snapshot.data['showShipping'] !== false;
        this.showBasket = this.activatedRoute.firstChild.snapshot.data['showBasket'] !== false;
        this.showNavigation = this.activatedRoute.firstChild.snapshot.data['showNavigation'] !== false;
        this.showFooter = this.activatedRoute.firstChild.snapshot.data['showFooter'] !== false && !this.kioskService.isKiosk;

        this.showFilter = false;
        if (this.activatedRoute.firstChild.snapshot.firstChild) {
          this.showFilter = !!this.activatedRoute.firstChild.snapshot.firstChild.data['showFilter'];
        }

        if (typeof window !== 'undefined') {
          window.scrollTo(0, 0);
        }
      }),
    );

    if (typeof window === 'undefined') {
      this.preconnect(this.contentUrl);
      this.preconnect(this.apiUrl);

      return;
    }

    const toggleNoScroll = (open: boolean) => {
      if (open) {
        this.renderer.addClass(this.document.body, 'no-scroll');
      } else {
        this.renderer.removeClass(this.document.body, 'no-scroll');
      }
    };

    this.subscription.add(
      this.basketOpen$.subscribe((open) => {
        toggleNoScroll(open);
      }),
    );

    this.subscription.add(
      this.mobileMenuOpen$.subscribe((open) => {
        toggleNoScroll(open);
      }),
    );

    // https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
    const setViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      this.document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setViewportHeight();
    this.subscription.add(fromEvent(window, 'resize').pipe(debounceTime(50)).subscribe(setViewportHeight));
  }

  initDialogs() {
    const badFocusTraps = this.document.querySelectorAll('.cdk-visually-hidden.cdk-focus-trap-anchor');
    badFocusTraps.forEach((trap) => {
      (trap as any).tabIndex = -1;
    });

    const startAsync = async () => {
      if (
        this.router.url.toLowerCase() !== this.cookiePolicyService.cookiePolicyUrl &&
        !this.cookiePolicyService.checkCookie() &&
        !this.cookieDialogOpen
      ) {
        this.cookieDialogOpen = true;
        await lastValueFrom(this.dialogService.openCookieConsentDialog().afterClosed());
        this.cookieDialogOpen = false;
      }

      const disclaimer = this.contentService.getDisclaimer();
      if (disclaimer) {
        if (!localStorage.getItem(this.disclaimerShown)) {
          localStorage.setItem(this.disclaimerShown, 'displayed');
          await lastValueFrom(this.dialogService.openDisclaimerDialog().afterClosed());
        }
      } else {
        localStorage.removeItem(this.disclaimerShown);
      }

      const infoPopup = this.contentService.getInfoPopup();
      if (infoPopup) {
        this.dialogService.openInfoPopup();
      }

      this.shippingService.loadSelectedShipping();
    };
    startAsync();

    this.subscription.add(
      this.router.events.subscribe((event) => {
        if (
          event instanceof NavigationEnd &&
          event.url.toLowerCase() !== this.cookiePolicyService.cookiePolicyUrl.toLowerCase() &&
          !this.cookiePolicyService.checkCookie() &&
          !this.cookieDialogOpen
        ) {
          this.cookieDialogOpen = true;
          this.dialogService
            .openCookieConsentDialog()
            .afterClosed()
            .pipe(take(1))
            .subscribe(() => {
              this.cookieDialogOpen = false;
            });
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  focusHeader(e: MouseEvent) {
    e.preventDefault();
    const main = this.document.getElementById('main');
    if (main) {
      main.focus();
      return;
    }
  }

  clearReset() {
    if (typeof window === 'undefined') {
      return;
    }

    // Used to limit reset attempts in index.html crash handler
    if (localStorage.getItem(INDEX_RESET_KEY)) {
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          localStorage.removeItem(INDEX_RESET_KEY);
        }, 1000 * 5);
      });
    }
  }

  setHeader() {
    this.showNavigationHeader = this.layoutService.isDesktop;
    this.stickyHeaderHeight =
      this.stickyHeader && this.showNavigationHeader ? (this.layoutService.isSmallDesktop ? '120px' : '150px') : null;
  }

  private setAnalytics() {
    const { type } = this.contentService.getMerchant();
    this.analyticService.setGoogleAnalytics(type);
  }

  private preconnect(url: string) {
    const head = this.document.head;

    const link = this.document.createElement('link');
    link.rel = 'preconnect';
    link.href = url;

    head.appendChild(link);
  }
}
